import React from 'react';
import { Box, Container, Skeleton, Stack } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';

function TopRowShimmer() {
    const { themeStyle } = useThemeContext();
    const bg = theme[themeStyle].gray_text;
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Skeleton variant="text" width={120} sx={{ fontSize: '0.8rem', bgcolor: bg }} />
                <Skeleton variant="rounded" width={180} height={28} sx={{ borderRadius: 999, bgcolor: bg }} />
            </Box>
            <Stack direction="row" spacing={1} sx={{ overflow: 'hidden' }}>
                {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton
                        key={i}
                        variant="rounded"
                        width={130}
                        height={170}
                        sx={{ borderRadius: '8px', bgcolor: bg, flexShrink: 0 }}
                    />
                ))}
            </Stack>
        </Box>
    );
}

function CategoryBarShimmer() {
    const { themeStyle } = useThemeContext();
    const bg = theme[themeStyle].gray_text;
    return (
        <Stack direction="row" spacing={1.5} sx={{ overflow: 'hidden' }}>
            {Array.from({ length: 7 }).map((_, i) => (
                <Skeleton
                    key={i}
                    variant="rounded"
                    width={i === 0 ? 50 : 110}
                    height={36}
                    sx={{ borderRadius: 999, bgcolor: bg, flexShrink: 0 }}
                />
            ))}
        </Stack>
    );
}

function CardShimmer() {
    const { themeStyle } = useThemeContext();
    const bg = theme[themeStyle].gray_text;
    const bgDark = theme[themeStyle].darkGray60;
    return (
        <Box
            sx={{
                borderRadius: '20px',
                bgcolor: '#131517',
                p: '20px 24px',
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
            }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: bgDark }} />
                    <Skeleton variant="text" width={80} sx={{ fontSize: '0.7rem', bgcolor: bg }} />
                    <Skeleton variant="rounded" width={60} height={24} sx={{ borderRadius: 999, bgcolor: bgDark }} />
                </Box>
                <Skeleton variant="text" width={90} sx={{ fontSize: '0.6rem', bgcolor: bg }} />
            </Box>
            {/* Feature image */}
            <Skeleton variant="rounded" height={200} sx={{ borderRadius: '10px', bgcolor: bgDark }} />
            {/* Info */}
            <Box
                sx={{
                    bgcolor: 'rgba(46,49,52,0.3)',
                    borderRadius: '4px',
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}>
                <Skeleton variant="text" width="70%" sx={{ fontSize: '1rem', bgcolor: bg }} />
                <Skeleton variant="text" width="50%" sx={{ fontSize: '0.8rem', bgcolor: bg }} />
                <Skeleton variant="text" width="40%" sx={{ fontSize: '0.8rem', bgcolor: bg }} />
            </Box>
            {/* CTA */}
            <Skeleton variant="rounded" height={42} sx={{ borderRadius: 999, bgcolor: bgDark }} />
        </Box>
    );
}

function SectionShimmer({ count = 4 }: { count?: number }) {
    const { themeStyle, device } = useThemeContext();
    const bg = theme[themeStyle].gray_text;
    const cols = device.isDesktop ? count : 2;
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Skeleton variant="text" width={160} sx={{ fontSize: '1.5rem', bgcolor: bg }} />
                <Skeleton variant="text" width={60} sx={{ fontSize: '0.9rem', bgcolor: bg }} />
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                        xl: `repeat(${count}, 1fr)`,
                    },
                }}>
                {Array.from({ length: cols }).map((_, i) => (
                    <CardShimmer key={i} />
                ))}
            </Box>
        </Box>
    );
}

const DiscoveryShimmer = () => {
    return (
        <Container
            disableGutters
            sx={{
                width: '100%',
                mt: { xs: '16px', sm: '24px' },
                ml: { xs: 0, sm: '16px', md: '24px' },
                px: { xs: '12px', sm: '20px', md: '24px' },
                overflowX: 'hidden',
            }}
            maxWidth={false}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px', width: '100%' }}>
                <TopRowShimmer />
                <CategoryBarShimmer />
                <SectionShimmer count={4} />
                <SectionShimmer count={4} />
                <SectionShimmer count={4} />
            </Box>
        </Container>
    );
};

export { DiscoveryShimmer };
